import React from "react";
import { Modal, Avatar, VbButton } from "@/components/vb";
import { Copy, Check } from "lucide-react";
import { renderMarkdownSafe } from "@/lib/markdown";
import { providerMeta } from "@/pages/vb/run/providerMeta";
import { modelFamily, displayUrl } from "./signalCardUtils";

type Citation = { id?: number; url?: string | null; response?: string | null };
type TechnicalSignal = {
  id?: number;
  model_used?: string;
  response?: string | null;
  citations?: Citation[];
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ts: TechnicalSignal | null;
};

/** Full-response dialog (AITS-492): the complete verbatim answer one model gave
 *  for an AI Trust Signal, its cited sources, and a Copy button. Mirrors the
 *  prototype's `.resp-dialog`; reuses the shared Radix `Modal` (re-whitened —
 *  `.vb-root` otherwise greys the panel). */
export function SignalResponseModal({ open, onOpenChange, ts }: Props) {
  const [copied, setCopied] = React.useState(false);

  // Reset the Copy affordance whenever a different response opens.
  React.useEffect(() => {
    if (open) setCopied(false);
  }, [open, ts?.id]);

  const family = modelFamily(ts?.model_used);
  const label = providerMeta(family).label;
  const answer = (ts?.response ?? "").trim();
  const citations = (ts?.citations ?? []).filter((c) => (c.url ?? "").trim().length > 0);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${label} — full response\n\n${answer}`);
      setCopied(true);
    } catch {
      /* clipboard blocked — leave the button in its default state */
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      srTitle={`${label} full response`}
      widthClassName="max-w-2xl"
      footer={
        ts ? (
          <div className="vb-sigresp__ft">
            <span className="vb-sigresp__ft-note">Captured during this report's scan.</span>
            <span className="vb-sigresp__ft-spacer" />
            <VbButton variant="secondary" size="sm" onClick={copy}>
              {copied ? <Check className="h-[13px] w-[13px]" /> : <Copy className="h-[13px] w-[13px]" />}
              {copied ? "Copied" : "Copy response"}
            </VbButton>
          </div>
        ) : undefined
      }
    >
      {ts && (
        <div className="vb-sigresp">
          <div className="vb-sigresp__hd">
            <Avatar model={family} size="md" decorative />
            <span className="vb-sigresp__name">{label}</span>
          </div>

          {answer ? (
            <div
              className="vb-sigresp__answer vb-md"
              dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(answer) }}
            />
          ) : (
            <p className="vb-sigresp__answer">No answer was returned by this model.</p>
          )}

          {citations.length > 0 && (
            <div className="vb-sigresp__sources">
              <div className="vb-sigresp__src-hd">
                Cited {citations.length} source{citations.length === 1 ? "" : "s"}
              </div>
              {citations.map((c, i) => {
                const { domain, path } = displayUrl(c.url);
                return (
                  <a
                    key={`${c.url}-${i}`}
                    className="vb-sigresp__src"
                    href={c.url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={c.url || undefined}
                  >
                    <span className="vb-sigresp__dot" aria-hidden="true" />
                    <span className="vb-sigresp__url">
                      <span className="vb-sigresp__domain">{domain}</span>
                      {path && <span className="vb-sigresp__path">{path}</span>}
                    </span>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default SignalResponseModal;
